import { useEffect, useState, useRef } from "preact/hooks";
import { useResponse, useNostoAppState } from "@nosto/search-js/preact/hooks";
import { pick } from "@nosto/search-js/utils";
import PopularSearches from "./PopularSearches";
import Keywords from "./Keywords";
import Categories from "./Categories";
import Products from "./Products";
import { useRovingFocus } from "@/hooks/useRovingFocus";
import { nostojs } from "@nosto/nosto-js";
import { JSONProduct } from "@nosto/nosto-js/client";
import { AutocompleteElement } from "@nosto/search-js/preact/autocomplete";
import DynamicCard from "@/elements/DynamicCard/DynamicCard";
import { cl } from "@nosto/search-js/utils";

type ResultsProps = {
  onKeyDown: (e: KeyboardEvent) => void;
};

const POPULAR_PRODUCTS_PLACEMENT_ID = "autocomplete-popular-products";
const SUGGESTIONS_PLACEMENT_ID = "autocomplete-suggestions";
const CACHE_KEY = "nosto_autocomplete_defaults";
const globalLanguage = window?.Bedrock?.language;

export default function Results({ onKeyDown }: ResultsProps) {
  const isFirstRender = useRef(true);
  const { loading, query } = useNostoAppState((state) =>
    pick(state, "loading", "query"),
  );
  const [title, setTitle] = useState<string | undefined>("");
  const [popularProducts, setPopularProducts] = useState<JSONProduct[]>([]);
  const [suggestions, setSuggestions] = useState("");
  const [isGridReady, setIsGridReady] = useState(false);
  const [showDefault, setShowDefault] = useState<boolean>(true);
  const [showResults, setShowResults] = useState<boolean>(false);
  const { categories, keywords, products, popularSearches } = useResponse();
  const containerRef = useRovingFocus<HTMLDivElement>(
    ".ns-autocomplete-element",
  );

  const hasResults = !!(
    categories?.hits?.length ||
    keywords?.hits?.length ||
    products?.hits?.length ||
    popularSearches?.hits?.length
  );

  const noResults = showResults && !hasResults && !loading;

  useEffect(() => {
    const cachedData = sessionStorage.getItem(CACHE_KEY);
    if (cachedData) {
      try {
        const parsedCache = JSON.parse(cachedData);
        setTitle(parsedCache.title);
        setPopularProducts(parsedCache.popularProducts);
        setSuggestions(parsedCache.suggestions);

        if (isFirstRender.current) {
          setShowDefault(true);
        }
        return;
      } catch (error) {
        console.warn("Failed to parse cached Nosto data", error);
      }
    }

    nostojs((api) => {
      api
        .defaultSession()
        .viewFrontPage()
        .setPlacements([
          POPULAR_PRODUCTS_PLACEMENT_ID,
          SUGGESTIONS_PLACEMENT_ID,
        ])
        .load()
        .then(async (response: any) => {
          const recommendations = response?.recommendations;
          if (!recommendations) return;
          const popular = recommendations[POPULAR_PRODUCTS_PLACEMENT_ID];
          const fetchedTitle = popular?.title;
          const fetchedProducts = popular?.products;
          const fetchedSuggestions =
            recommendations[SUGGESTIONS_PLACEMENT_ID].html;

          setTitle(fetchedTitle);
          setPopularProducts(fetchedProducts);
          setSuggestions(fetchedSuggestions);

          sessionStorage.setItem(
            CACHE_KEY,
            JSON.stringify({
              title: fetchedTitle,
              popularProducts: fetchedProducts,
              suggestions: fetchedSuggestions,
            }),
          );

          if (isFirstRender.current) {
            setShowDefault(true);
          }
        });
    });
  }, []);

  useEffect(() => {
    if (popularProducts?.length > 0) {
      setIsGridReady(false);
      const timer = setTimeout(() => {
        setIsGridReady(true);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [popularProducts]);

  useEffect(() => {
    if (isFirstRender.current) {
      setShowDefault(true);
      setShowResults(false);
      return;
    }
    if (!loading && query.query) {
      setShowResults(true);
      setShowDefault(false);
    } else if (!query.query) {
      setShowResults(false);
      setShowDefault(true);
    }
  }, [loading, query.query]);

  useEffect(() => {
    const timer = setTimeout(() => {
      isFirstRender.current = false;
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="search-dropdown-panel"
      data-nosto-element="autocomplete"
      onKeyDown={onKeyDown}
      ref={containerRef}
    >
      {noResults ? (
        <div className="search-dropdown-panel__no-results">
          {globalLanguage?.no_results_for || "No results for"} &quot;
          {query.query}
          &quot;
        </div>
      ) : (
        <>
          <div className="search-dropdown-panel__left">
            {showResults && hasResults && (
              <>
                <div>
                  <h4>Related Search</h4>
                  <PopularSearches searches={popularSearches} />
                  <Keywords keywords={keywords} searches={popularSearches} />
                </div>
                <Categories categories={categories} />
              </>
            )}
            {showDefault && suggestions ? (
              <div
                className="search-dropdown-panel__top-suggestions"
                dangerouslySetInnerHTML={{ __html: suggestions }}
              />
            ) : (
              <></>
            )}
          </div>
          <div className="search-dropdown-panel__right">
            {showDefault && popularProducts ? (
              <>
                <h4>{title}</h4>
                <div
                  className={cl(
                    "search-dropdown-panel__grid",
                    isGridReady && "search-dropdown-panel__grid--is-loaded",
                  )}
                >
                  {popularProducts.map((hit) => (
                    <AutocompleteElement
                      key={hit.product_id}
                      as={DynamicCard}
                      hit={{
                        productId: hit.product_id!,
                        url: hit.url,
                      }}
                      componentProps={{
                        handle: hit.handle!,
                        section: "nosto-product-tile",
                        className: "ns-autocomplete-element",
                        itemType: "autocomplete",
                      }}
                    />
                  ))}
                </div>
              </>
            ) : (
              <></>
            )}
            {showResults && (
              <Products products={products} query={query.query || ""} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
